import { _isNil, _notNil } from '@/littledash';
import type {
  AuditLogAnimalSummary,
  AuditLogEntitySummary as Summary,
} from '@/components/Studies/AuditLog/AuditLog.model';

const animalFromEntity = (summary: Summary | undefined): AuditLogAnimalSummary | null => {
  const entity = summary?.entity;
  if (_isNil(entity)) {
    return null;
  }
  switch (summary?.entity_type) {
    case 'ANIMAL':
      return entity as AuditLogAnimalSummary;
    case 'SAMPLE':
    case 'DOSAGE':
    case 'OBSERVATION':
    case 'MEASUREMENT': {
      const withAnimal = entity as { animal?: AuditLogAnimalSummary | null };
      return withAnimal.animal ?? null;
    }
    default:
      return null;
  }
};

export const animalSubjectIdFromEntity = (summary: Summary | undefined): number | null =>
  animalFromEntity(summary)?.id ?? null;

/**
 * For ANIMAL rows the top-level `deleted_at` mirrors the inner animal's
 * `deleted_at`; for child entities only the inner one is authoritative
 * (top-level is the child's own deletion, not the animal's).
 */
export const isLinkedAnimalDeleted = (summary: Summary | undefined): boolean => {
  if (summary?.entity_type === 'ANIMAL' && _notNil(summary?.deleted_at)) {
    return true;
  }
  return _notNil(animalFromEntity(summary)?.deleted_at);
};

/**
 * Which tab of the animal profile page to open when jumping from an audit-log
 * modal. Mirrors the sync-entity-details modal so users land on the section
 * that corresponds to the audited entity.
 */
export const tabForEntityType = (
  entityType: Summary['entity_type']
): 'overview' | 'samples' | 'dosing' | 'observations' => {
  switch (entityType) {
    case 'SAMPLE':
      return 'samples';
    case 'DOSAGE':
      return 'dosing';
    case 'OBSERVATION':
      return 'observations';
    case 'ANIMAL':
    case 'MEASUREMENT':
    default:
      return 'overview';
  }
};

/**
 * Human-readable label for the "jump to animal" link. Mirrors the sync-entity
 * details modal's phrasing so the two flows feel consistent.
 */
export const animalLinkLabelForEntityType = (entityType: Summary['entity_type']): string => {
  switch (entityType) {
    case 'SAMPLE':
      return 'Go to animal samples';
    case 'DOSAGE':
      return 'Go to animal dosages';
    case 'OBSERVATION':
      return 'Go to animal observations';
    case 'ANIMAL':
    case 'MEASUREMENT':
    default:
      return 'Go to animal profile';
  }
};
